// Spanish (/es) and Brazilian Portuguese (/pt) landing pages, rendered by
// components/hub/HeadTermHub.tsx through pages/hub/[slug].tsx (next.config.mjs
// rewrites the slugs below there). The /es and /pt homes are the translated
// homepage (pages/index.tsx).
//
// Each page targets what people actually type, from Google autocomplete and
// OpenSEO volumes (Oct 2026, monthly searches: MX = Mexico, ES = Spain,
// BR = Brazil). Volumes are noted per page so the next person knows why a
// heading says what it says.
//
// Rules carried over from hubPages.ts, plus two from the research:
// - "any voice", never "any song"; remind people they need rights to audio.
// - Free is browsing and listening only. Searchers add "gratis"/"grátis" to
//   almost everything; answer it honestly in the FAQ, never promise it.
// - Many voice-changer searches want real-time, phone-call or WhatsApp
//   changing. Jammable converts recorded audio: say so, don't imply otherwise.
// - Skipped on purpose: "voz ia" (MX 8.1k, BR 8.1k) and "crear canción con IA"
//   (MX 5.4k) return text-to-speech and song-generator results, not ours.
import type { HubCategory, HubConfig } from "./hubPages";
import type { Locale } from "@jammable/ui-core/src/i18n";
import { HUB_SLUGS } from "./hubGroups";
import { HUBS as FR } from "./localeHubs/fr";
import { HUBS as DE } from "./localeHubs/de";
import { HUBS as JA } from "./localeHubs/ja";
import { HUBS as KO } from "./localeHubs/ko";
import { HUBS as ID } from "./localeHubs/id";
import { HUBS as TR } from "./localeHubs/tr";

export const CATEGORIES_ES: HubCategory[] = [
  { label: "Música", filter: "music" },
  { label: "Anime", filter: "anime" },
  { label: "Caricaturas", filter: "cartoon" },
  { label: "Videojuegos", filter: "gaming" },
  { label: "K-pop", filter: "korean" },
  { label: "Famosos", filter: "public figure" },
];

const CATEGORIES_PT: HubCategory[] = [
  { label: "Música", filter: "music" },
  { label: "Anime", filter: "anime" },
  { label: "Desenhos", filter: "cartoon" },
  { label: "Games", filter: "gaming" },
  { label: "K-pop", filter: "korean" },
  { label: "Famosos", filter: "public figure" },
];

const FREE_ES = {
  q: "¿Jammable es gratis?",
  a: "Explorar todas las voces y escuchar los covers de ejemplo es gratis. Para crear covers, cambiar tu voz, clonar una voz o usar texto a voz necesitas un plan. Consulta la página de precios para ver las opciones actuales.",
};
const FREE_PT = {
  q: "O Jammable é grátis?",
  a: "Explorar todas as vozes e ouvir os covers de exemplo é grátis. Para criar covers, mudar sua voz, clonar uma voz ou usar texto para fala, você precisa de um plano. Veja a página de preços para as opções atuais.",
};

// ─────────────────────────────── Spanish ───────────────────────────────

const ES: Record<string, HubConfig> = {
  // "covers ia" MX 1k / ES 390, "cover ia" MX 480, "ai cover(s)" MX 960,
  // "cover ia gratis" MX 210, "cover con ia" MX 110. Autocomplete long tail:
  // "cambiar voz de una canción por la mía", "covers ia con mi voz",
  // "canción con voz de otro artista".
  "covers-con-ia": {
    slug: "covers-con-ia",
    group: "cover",
    path: "/es/covers-con-ia",
    locale: "es",
    h1: "Covers con IA",
    highlight: "IA",
    title: "Covers con IA: Haz Covers IA con Cualquier Voz | Jammable",
    metaDescription:
      "Haz covers con IA con más de 60 mil voces: cantantes, personajes y famosos. Cambia la voz de un audio por otra en segundos, con calidad de estudio.",
    heroSub:
      "Elige una voz, agrega un audio que tengas derecho a usar y Jammable te devuelve un cover IA en segundos.",
    intro:
      "Antes, cambiar la voz de una canción requería un estudio y horas de mezcla. Con Jammable eliges la voz, subes tu audio y la IA hace el resto: separa la voz, la convierte y la vuelve a mezclar.",
    appName: "Jammable Covers con IA",
    steps: [
      {
        title: "Elige una voz",
        body: "Más de 60 mil voces IA: cantantes, raperos, personajes de caricaturas y anime, o una voz que entrenes tú.",
      },
      {
        title: "Agrega tu audio",
        body: "Sube un archivo, grábate cantando o genera una canción original con IA.",
      },
      {
        title: "Crea tu cover",
        body: "Jammable convierte la voz y mezcla el resultado. Descárgalo y compártelo en TikTok o YouTube.",
      },
    ],
    faqs: [
      {
        q: "¿Qué es un cover con IA?",
        a: "Es una versión de un tema interpretada con otra voz gracias a la IA. Tomas una pista o tu propia voz y la conviertes en la voz de un artista, un personaje o una voz que entrenaste, manteniendo la melodía y la letra.",
      },
      {
        q: "¿Cómo cambio la voz de una canción por otra?",
        a: "Elige la voz en Jammable, sube el audio y presiona crear. Jammable separa la voz de la música, la convierte a la voz elegida y vuelve a mezclar todo en menos de un minuto. Usa solo audio que tengas derecho a usar.",
      },
      {
        q: "¿Puedo hacer covers IA con mi propia voz?",
        a: "Sí. Entrena un modelo con tu voz a partir de al menos 6 minutos de audio limpio y úsalo en cualquier cover.",
      },
      FREE_ES,
      {
        q: "¿Qué tan bien suenan los covers con IA?",
        a: "Pueden sonar muy cerca de una grabación real. Jammable usa conversión de voz de alta calidad y mezcla automática; los planes de pago ofrecen ajustes de mayor calidad.",
      },
    ],
    categories: CATEGORIES_ES,
    related: [
      { label: "Voces IA de famosos", href: "/es/voces-ia-de-famosos" },
      { label: "Cambiador de voz IA", href: "/es/cambiador-de-voz-ia" },
      { label: "Clonar voz con IA", href: "/es/clonar-voz-ia" },
    ],
  },

  // "cambiador de voz" MX 2.9k / ES 1.6k, "modificador de voz" MX 1k,
  // "cambiador de voz ia" MX 140, "cambiar mi voz con ia". Many related
  // searches want real-time / phone calls: answered honestly in the FAQ.
  "cambiador-de-voz-ia": {
    slug: "cambiador-de-voz-ia",
    group: "voice-changer",
    path: "/es/cambiador-de-voz-ia",
    locale: "es",
    h1: "Cambiador de voz IA",
    highlight: "voz IA",
    title:
      "Cambiador de Voz IA: Cambia tu Voz por la de Quien Quieras | Jammable",
    metaDescription:
      "Cambiador y modificador de voz con IA: convierte tu voz en la de un personaje, cantante o famoso. Más de 60 mil voces realistas. Graba o sube un audio.",
    heroSub:
      "Graba o sube un audio, elige a quién quieres sonar y escucha tu voz transformada en segundos.",
    intro:
      "Un cambiador de voz con IA no solo distorsiona tu voz: la convierte en otra voz real. Personajes en español latino, cantantes, famosos o una voz que entrenes tú.",
    appName: "Jammable Cambiador de Voz IA",
    steps: [
      {
        title: "Graba o sube",
        body: "Grábate con el micrófono o sube el audio que quieres transformar.",
      },
      {
        title: "Elige una voz",
        body: "Más de 60 mil voces IA, de personajes y caricaturas a cantantes y famosos.",
      },
      {
        title: "Convierte tu voz",
        body: "En segundos tienes el audio con la nueva voz, listo para descargar.",
      },
    ],
    faqs: [
      {
        q: "¿Qué es un cambiador de voz con IA?",
        a: "Es una herramienta que toma un audio grabado y lo convierte para que suene como otra voz, usando conversión de voz con IA. A diferencia de un modificador de voz con efectos, el resultado suena como la voz real elegida.",
      },
      {
        q: "¿Cómo cambio mi voz con IA?",
        a: "Graba o sube un audio, elige la voz que quieres y presiona crear. Jammable convierte tu audio y te da un resultado limpio para descargar.",
      },
      {
        q: "¿Funciona en tiempo real o en llamadas?",
        a: "No. Jammable convierte audio grabado o subido, no el micrófono en vivo ni las llamadas. Así el resultado es mucho más realista que con un cambiador de voz en tiempo real.",
      },
      {
        q: "¿Puedo cambiar mi voz a la de un personaje?",
        a: "Sí. Hay miles de voces de personajes, caricaturas y videojuegos, incluidas cientos de voces de doblaje latino, y cada día se agregan nuevas.",
      },
      FREE_ES,
    ],
    categories: CATEGORIES_ES,
    related: [
      { label: "Voces IA de personajes", href: "/es/voces-ia-de-personajes" },
      { label: "Covers con IA", href: "/es/covers-con-ia" },
      { label: "Clonar voz con IA", href: "/es/clonar-voz-ia" },
    ],
  },

  // "voz ia de personajes" MX 720 (+ "español latino" 170; already 765
  // impressions/28d at position 2.4 for that), "voz de personajes ia" 90.
  // The grid shows the "(Latino)" dubs: 400+ exist (Mordecai, Finn, Peppa,
  // Goku, Bob Esponja, Homero...).
  "voces-ia-de-personajes": {
    slug: "voces-ia-de-personajes",
    group: "characters",
    path: "/es/voces-ia-de-personajes",
    locale: "es",
    h1: "Voces IA de personajes en español latino",
    highlight: "personajes",
    title: "Voces IA de Personajes en Español Latino | Jammable",
    metaDescription:
      "Voces IA de personajes en español latino: caricaturas, anime y videojuegos con su voz de doblaje. Haz covers y audios con la voz de tus personajes favoritos.",
    heroSub:
      "Goku, Homero, Bob Esponja, Mordecai y cientos más con su voz de doblaje latino. Elige uno y hazlo cantar o hablar.",
    intro:
      "La comunidad de Jammable ha creado cientos de voces IA de personajes con su doblaje en español latino. Úsalas para covers, memes o audios para tus videos.",
    appName: "Jammable Voces IA de Personajes",
    steps: [
      {
        title: "Elige un personaje",
        body: "Busca tu personaje favorito por nombre. Hay voces de caricaturas, anime y videojuegos.",
      },
      {
        title: "Agrega tu audio",
        body: "Sube una canción o grábate hablando. También puedes escribir un texto.",
      },
      {
        title: "Escucha a tu personaje",
        body: "Jammable convierte la voz en segundos. Descarga el audio y compártelo.",
      },
    ],
    faqs: [
      {
        q: "¿Qué voces de personajes hay en español latino?",
        a: 'Cientos, creadas por la comunidad: personajes de caricaturas como Bob Esponja, Homero Simpson o Mordecai, de anime como Goku, y muchos más. Busca el nombre del personaje junto con "latino".',
      },
      {
        q: "¿Puedo hacer que un personaje cante una canción?",
        a: "Sí. Sube el audio, elige el personaje y Jammable convierte la voz manteniendo la melodía. Usa solo audio que tengas derecho a usar.",
      },
      {
        q: "¿Puedo escribir un texto y que lo diga un personaje?",
        a: "Sí, con texto a voz: escribe hasta 2,500 caracteres y escúchalos con la voz del personaje.",
      },
      {
        q: "¿Y si no encuentro mi personaje?",
        a: "Cada día se suben voces nuevas. Si tienes al menos 6 minutos de audio limpio del personaje, puedes entrenar la voz tú mismo.",
      },
      FREE_ES,
    ],
    categories: CATEGORIES_ES,
    related: [
      { label: "Cambiador de voz IA", href: "/es/cambiador-de-voz-ia" },
      { label: "Voces IA de famosos", href: "/es/voces-ia-de-famosos" },
      { label: "Covers con IA", href: "/es/covers-con-ia" },
    ],
    voices: { nameRegex: "latino", allTime: true },
  },

  // "voz ia de famosos" MX 480 (+ "gratis" 170), "voz de famosos ia" 170,
  // autocomplete: "poner voz de cantantes ia", "hacer canción con voz de
  // famosos", "canción con voz de otro artista".
  "voces-ia-de-famosos": {
    slug: "voces-ia-de-famosos",
    group: "celebrities",
    path: "/es/voces-ia-de-famosos",
    locale: "es",
    h1: "Voces IA de famosos y cantantes",
    highlight: "famosos",
    title: "Voces IA de Famosos y Cantantes | Jammable",
    metaDescription:
      "Voces IA de famosos y cantantes para tus covers: artistas, raperos y celebridades creados por la comunidad. Pon la voz de tu cantante favorito a un audio.",
    heroSub:
      "Pon la voz de un cantante o famoso a tu audio. Miles de voces de artistas creadas por la comunidad.",
    intro:
      "¿Quieres escuchar una canción con la voz de otro artista? Elige entre miles de voces IA de cantantes, raperos y famosos, sube tu audio y Jammable hace el cambio.",
    appName: "Jammable Voces IA de Famosos",
    steps: [
      {
        title: "Elige un famoso",
        body: "Busca al cantante o famoso por su nombre entre miles de voces.",
      },
      { title: "Agrega tu audio", body: "Sube una pista o grábate cantando." },
      {
        title: "Escucha el resultado",
        body: "Jammable convierte la voz y mezcla la pista en segundos.",
      },
    ],
    faqs: [
      {
        q: "¿Cómo pongo la voz de un cantante a una canción?",
        a: "Elige la voz del cantante, sube el audio y presiona crear. Jammable separa la voz, la convierte y vuelve a mezclar la pista. Usa solo audio que tengas derecho a usar.",
      },
      {
        q: "¿Las voces de famosos son oficiales?",
        a: "No. Son modelos creados por la comunidad y no están afiliados a los artistas. Úsalos con respeto y no para engañar a nadie.",
      },
      {
        q: "¿Puedo usar los covers en redes sociales?",
        a: "Revisa los derechos del audio original y las reglas de cada plataforma. Indicar que es un cover con IA es una buena práctica.",
      },
      FREE_ES,
    ],
    categories: CATEGORIES_ES,
    related: [
      { label: "Covers con IA", href: "/es/covers-con-ia" },
      { label: "Voces IA de personajes", href: "/es/voces-ia-de-personajes" },
      { label: "Clonar voz con IA", href: "/es/clonar-voz-ia" },
    ],
    voices: { badge: "music", allTime: true },
  },

  // "clonar voz ia" MX 1.6k / ES 880, "clonar voz" MX 1.3k,
  // "imitar voz con ia" MX 110.
  "clonar-voz-ia": {
    slug: "clonar-voz-ia",
    group: "clone",
    path: "/es/clonar-voz-ia",
    locale: "es",
    h1: "Clonar voz con IA",
    highlight: "voz",
    title: "Clonar Voz con IA: Crea un Modelo de Tu Voz | Jammable",
    metaDescription:
      "Clona tu voz o cualquier voz con IA a partir de 6 minutos de audio. Usa tu voz clonada en covers, audios y texto a voz. Sube modelos RVC propios.",
    heroSub:
      "Sube al menos 6 minutos de audio limpio y Jammable entrena un modelo de esa voz para usarlo en covers y texto a voz.",
    intro:
      "Clonar una voz con IA significa entrenar un modelo que aprende cómo suena. Después puedes hacer que esa voz cante o hable lo que quieras. Clona solo tu voz o voces que tengas permiso de usar.",
    appName: "Jammable Clonar Voz IA",
    steps: [
      {
        title: "Sube tu audio",
        body: "Al menos 6 minutos de voz cantada o hablada, limpia y sin silencios largos.",
      },
      {
        title: "Entrena el modelo",
        body: "Jammable entrena la voz por ti. No necesitas tarjeta gráfica ni conocimientos técnicos.",
      },
      {
        title: "Usa tu voz",
        body: "Haz covers, convierte audios o escribe un texto y escúchalo con tu voz clonada.",
      },
    ],
    faqs: [
      {
        q: "¿Cómo clonar una voz con IA?",
        a: "Sube al menos 6 minutos de audio limpio de la voz, ponle un nombre y Jammable entrena el modelo. Cuando termina, la voz aparece en tu biblioteca lista para usar.",
      },
      {
        q: "¿Cuánto audio necesito para clonar una voz?",
        a: "Mínimo 6 minutos de voz limpia, sin música de fondo ni silencios largos. Más audio y de mejor calidad da un resultado más fiel.",
      },
      {
        q: "¿Puedo subir un modelo RVC que ya tengo?",
        a: "Sí. Sube los archivos .pth e .index desde tu biblioteca y la voz queda lista al instante.",
      },
      {
        q: "¿Puedo clonar la voz de otra persona?",
        a: "Solo si tienes su permiso. Clona tu propia voz o voces que tengas derecho a usar.",
      },
      FREE_ES,
    ],
    categories: CATEGORIES_ES,
    related: [
      { label: "Entrenar mi voz", href: "/train" },
      { label: "Covers con IA", href: "/es/covers-con-ia" },
      { label: "Cambiador de voz IA", href: "/es/cambiador-de-voz-ia" },
    ],
  },
};

// ─────────────────────────── Portuguese (Brazil) ───────────────────────────

const PT: Record<string, HubConfig> = {
  // "covers ia" BR 1k, "cover ai" 1k, "cover ia" 480, "cover ia grátis" 170,
  // "cover ia com minha voz" 50. Autocomplete: "mudar a voz de uma música com
  // ia", "trocar voz de cantor com ia", "usar voz de cantor ia".
  "covers-ia": {
    slug: "covers-ia",
    group: "cover",
    path: "/pt/covers-ia",
    locale: "pt",
    h1: "Covers com IA",
    highlight: "IA",
    title: "Covers com IA: Faça Cover IA com Qualquer Voz | Jammable",
    metaDescription:
      "Faça covers com IA usando mais de 60 mil vozes: cantores, personagens e famosos. Mude a voz de uma música em segundos, com qualidade de estúdio.",
    heroSub:
      "Escolha uma voz, adicione um áudio que você tem direito de usar e o Jammable devolve um cover IA em segundos.",
    intro:
      "Mudar a voz de uma música antes exigia estúdio e horas de mixagem. No Jammable você escolhe a voz, envia o áudio e a IA faz o resto: separa a voz, converte e mixa de novo.",
    appName: "Jammable Covers com IA",
    steps: [
      {
        title: "Escolha uma voz",
        body: "Mais de 60 mil vozes de IA: cantores, rappers, personagens de desenhos e anime, ou uma voz treinada por você.",
      },
      {
        title: "Adicione seu áudio",
        body: "Envie um arquivo, grave você cantando ou gere uma música original com IA.",
      },
      {
        title: "Crie seu cover",
        body: "O Jammable converte a voz e mixa o resultado. Baixe e poste no TikTok ou no YouTube.",
      },
    ],
    faqs: [
      {
        q: "O que é um cover com IA?",
        a: "É uma versão de uma faixa cantada com outra voz usando IA. Você pega uma faixa ou sua própria voz e converte para a voz de um artista, um personagem ou uma voz que você treinou, mantendo a melodia e a letra.",
      },
      {
        q: "Como mudar a voz de uma música com IA?",
        a: "Escolha a voz no Jammable, envie o áudio e clique em criar. O Jammable separa a voz da música, converte para a voz escolhida e mixa tudo em menos de um minuto. Use só áudio que você tem direito de usar.",
      },
      {
        q: "Posso fazer cover IA com a minha voz?",
        a: "Sim. Treine um modelo com sua voz a partir de pelo menos 6 minutos de áudio limpo e use em qualquer cover.",
      },
      FREE_PT,
      {
        q: "Covers com IA soam bem?",
        a: "Podem soar muito próximos de uma gravação real. O Jammable usa conversão de voz de alta qualidade e mixagem automática; os planos pagos têm ajustes de qualidade ainda maior.",
      },
    ],
    categories: CATEGORIES_PT,
    related: [
      { label: "Vozes de famosos", href: "/pt/vozes-de-famosos-ia" },
      { label: "Modificador de voz", href: "/pt/modificador-de-voz" },
      { label: "Clonar voz com IA", href: "/pt/clonar-voz-ia" },
    ],
  },

  // "modificador de voz" BR 14.8k (largest matching term in any market),
  // "mudar voz com ia" 720, "mudar voz" 720. Related searches want WhatsApp,
  // Discord, Waze and calls: answered honestly in the FAQ.
  "modificador-de-voz": {
    slug: "modificador-de-voz",
    group: "voice-changer",
    path: "/pt/modificador-de-voz",
    locale: "pt",
    h1: "Modificador de voz com IA",
    highlight: "voz",
    title:
      "Modificador de Voz com IA: Mude Sua Voz para Qualquer Uma | Jammable",
    metaDescription:
      "Modificador de voz com IA: transforme sua voz na de um personagem, cantor ou famoso. Mais de 60 mil vozes realistas. Grave ou envie um áudio.",
    heroSub:
      "Grave ou envie um áudio, escolha com quem você quer parecer e ouça sua voz transformada em segundos.",
    intro:
      "Um modificador de voz com IA não só distorce sua voz: ele transforma em outra voz de verdade. Personagens, cantores, famosos ou uma voz que você mesmo treinou.",
    appName: "Jammable Modificador de Voz",
    steps: [
      {
        title: "Grave ou envie",
        body: "Grave com o microfone ou envie o áudio que você quer transformar.",
      },
      {
        title: "Escolha uma voz",
        body: "Mais de 60 mil vozes de IA, de personagens e desenhos a cantores e famosos.",
      },
      {
        title: "Mude sua voz",
        body: "Em segundos você tem o áudio com a nova voz, pronto para baixar.",
      },
    ],
    faqs: [
      {
        q: "O que é um modificador de voz com IA?",
        a: "É uma ferramenta que pega um áudio gravado e converte para soar como outra voz, usando conversão de voz por IA. Diferente de um modificador com efeitos, o resultado soa como a voz real escolhida.",
      },
      {
        q: "Como mudar minha voz com IA?",
        a: "Grave ou envie um áudio, escolha a voz que você quer e clique em criar. O Jammable converte o áudio e entrega um resultado limpo para baixar.",
      },
      {
        q: "Funciona em tempo real, no WhatsApp ou em ligações?",
        a: "Não. O Jammable converte áudio gravado ou enviado, não o microfone ao vivo nem ligações. Por isso o resultado é muito mais realista do que um modificador de voz em tempo real. Você pode baixar o áudio e enviar onde quiser.",
      },
      {
        q: "Posso mudar minha voz para a de um personagem?",
        a: "Sim. Há milhares de vozes de personagens, desenhos e games, e novas vozes chegam todos os dias.",
      },
      FREE_PT,
    ],
    categories: CATEGORIES_PT,
    related: [
      { label: "Vozes de personagens", href: "/pt/vozes-de-personagens-ia" },
      { label: "Covers com IA", href: "/pt/covers-ia" },
      { label: "Clonar voz com IA", href: "/pt/clonar-voz-ia" },
    ],
  },

  // "voz de personagens ia" BR 880, "voz de personagens de anime ia" 50,
  // "voz do stitch" 1k, "voz do bob esponja" 590.
  "vozes-de-personagens-ia": {
    slug: "vozes-de-personagens-ia",
    group: "characters",
    path: "/pt/vozes-de-personagens-ia",
    locale: "pt",
    h1: "Vozes de personagens com IA",
    highlight: "personagens",
    title: "Vozes de Personagens com IA: Desenhos, Anime e Games | Jammable",
    metaDescription:
      "Vozes de personagens com IA: desenhos, anime e games. Faça covers e áudios com a voz do Stitch, do Bob Esponja, do Goku e de milhares de outros.",
    heroSub:
      "Stitch, Bob Esponja, Goku e milhares de outros personagens. Escolha um e faça ele cantar ou falar.",
    intro:
      "A comunidade do Jammable criou milhares de vozes de IA de personagens de desenhos, anime e games. Use em covers, memes ou áudios para seus vídeos.",
    appName: "Jammable Vozes de Personagens",
    steps: [
      {
        title: "Escolha um personagem",
        body: "Busque seu personagem favorito pelo nome. Há vozes de desenhos, anime e games.",
      },
      {
        title: "Adicione seu áudio",
        body: "Envie uma música ou grave você falando. Também dá para digitar um texto.",
      },
      {
        title: "Ouça o personagem",
        body: "O Jammable converte a voz em segundos. Baixe o áudio e compartilhe.",
      },
    ],
    faqs: [
      {
        q: "Quais vozes de personagens existem?",
        a: "Milhares, criadas pela comunidade: personagens de desenhos como Bob Esponja e Stitch, de anime como Goku e Zoro, de games e muito mais. Busque pelo nome do personagem.",
      },
      {
        q: "Tem vozes com a dublagem brasileira?",
        a: "Algumas, e a comunidade sobe novas todos os dias. Se você tem pelo menos 6 minutos de áudio limpo da dublagem, pode treinar a voz você mesmo.",
      },
      {
        q: "Posso fazer um personagem cantar uma música?",
        a: "Sim. Envie o áudio, escolha o personagem e o Jammable converte a voz mantendo a melodia. Use só áudio que você tem direito de usar.",
      },
      {
        q: "Posso digitar um texto e ouvir na voz do personagem?",
        a: "Sim, com texto para fala: digite até 2.500 caracteres e ouça na voz do personagem.",
      },
      FREE_PT,
    ],
    categories: CATEGORIES_PT,
    related: [
      { label: "Modificador de voz", href: "/pt/modificador-de-voz" },
      { label: "Vozes de famosos", href: "/pt/vozes-de-famosos-ia" },
      { label: "Covers com IA", href: "/pt/covers-ia" },
    ],
    voices: { badge: "cartoon", allTime: true },
  },

  // "voz de famosos ia" BR 590, "voz ia de famosos" 390,
  // "ia que canta com voz de famosos" 90.
  "vozes-de-famosos-ia": {
    slug: "vozes-de-famosos-ia",
    group: "celebrities",
    path: "/pt/vozes-de-famosos-ia",
    locale: "pt",
    h1: "Vozes de famosos com IA",
    highlight: "famosos",
    title: "Vozes de Famosos com IA: Cantores e Celebridades | Jammable",
    metaDescription:
      "Vozes de famosos com IA para seus covers: cantores, rappers e celebridades criados pela comunidade. Coloque a voz do seu cantor favorito em um áudio.",
    heroSub:
      "Coloque a voz de um cantor ou famoso no seu áudio. Milhares de vozes de artistas criadas pela comunidade.",
    intro:
      "Quer ouvir uma música com a voz de outro artista? Escolha entre milhares de vozes de IA de cantores, rappers e famosos, envie seu áudio e o Jammable faz a troca.",
    appName: "Jammable Vozes de Famosos",
    steps: [
      {
        title: "Escolha um famoso",
        body: "Busque o cantor ou famoso pelo nome entre milhares de vozes.",
      },
      {
        title: "Adicione seu áudio",
        body: "Envie uma faixa ou grave você cantando.",
      },
      {
        title: "Ouça o resultado",
        body: "O Jammable converte a voz e mixa a faixa em segundos.",
      },
    ],
    faqs: [
      {
        q: "Como colocar a voz de um cantor em uma música?",
        a: "Escolha a voz do cantor, envie o áudio e clique em criar. O Jammable separa a voz, converte e mixa a faixa de novo. Use só áudio que você tem direito de usar.",
      },
      {
        q: "As vozes de famosos são oficiais?",
        a: "Não. São modelos criados pela comunidade, sem ligação com os artistas. Use com respeito e nunca para enganar ninguém.",
      },
      {
        q: "Posso postar os covers nas redes sociais?",
        a: "Confira os direitos do áudio original e as regras de cada plataforma. Avisar que é um cover com IA é uma boa prática.",
      },
      FREE_PT,
    ],
    categories: CATEGORIES_PT,
    related: [
      { label: "Covers com IA", href: "/pt/covers-ia" },
      { label: "Vozes de personagens", href: "/pt/vozes-de-personagens-ia" },
      { label: "Clonar voz com IA", href: "/pt/clonar-voz-ia" },
    ],
    voices: { badge: "music", allTime: true },
  },

  // "clonar voz" BR 5.4k, "clonar voz ia" 3.6k, "imitar voz ia" 90.
  "clonar-voz-ia": {
    slug: "clonar-voz-ia",
    group: "clone",
    path: "/pt/clonar-voz-ia",
    locale: "pt",
    h1: "Clonar voz com IA",
    highlight: "voz",
    title: "Clonar Voz com IA: Crie um Modelo da Sua Voz | Jammable",
    metaDescription:
      "Clone sua voz ou qualquer voz com IA a partir de 6 minutos de áudio. Use sua voz clonada em covers, áudios e texto para fala. Envie seus modelos RVC.",
    heroSub:
      "Envie pelo menos 6 minutos de áudio limpo e o Jammable treina um modelo dessa voz para usar em covers e texto para fala.",
    intro:
      "Clonar uma voz com IA é treinar um modelo que aprende como ela soa. Depois você pode fazer essa voz cantar ou falar o que quiser. Clone só sua voz ou vozes que você tem permissão para usar.",
    appName: "Jammable Clonar Voz",
    steps: [
      {
        title: "Envie seu áudio",
        body: "Pelo menos 6 minutos de voz cantada ou falada, limpa e sem silêncios longos.",
      },
      {
        title: "Treine o modelo",
        body: "O Jammable treina a voz para você. Não precisa de placa de vídeo nem de conhecimento técnico.",
      },
      {
        title: "Use sua voz",
        body: "Faça covers, converta áudios ou digite um texto e ouça com sua voz clonada.",
      },
    ],
    faqs: [
      {
        q: "Como clonar uma voz com IA?",
        a: "Envie pelo menos 6 minutos de áudio limpo da voz, dê um nome e o Jammable treina o modelo. Quando termina, a voz aparece na sua biblioteca pronta para usar.",
      },
      {
        q: "Quanto áudio preciso para clonar uma voz?",
        a: "No mínimo 6 minutos de voz limpa, sem música de fundo nem silêncios longos. Mais áudio e de melhor qualidade dá um resultado mais fiel.",
      },
      {
        q: "Posso enviar um modelo RVC que já tenho?",
        a: "Sim. Envie os arquivos .pth e .index pela sua biblioteca e a voz fica pronta na hora.",
      },
      {
        q: "Posso clonar a voz de outra pessoa?",
        a: "Só com a permissão dela. Clone sua própria voz ou vozes que você tem direito de usar.",
      },
      FREE_PT,
    ],
    categories: CATEGORIES_PT,
    related: [
      { label: "Treinar minha voz", href: "/train" },
      { label: "Covers com IA", href: "/pt/covers-ia" },
      { label: "Modificador de voz", href: "/pt/modificador-de-voz" },
    ],
  },
};

export const LOCALE_HUBS: Record<
  Exclude<Locale, "en">,
  Record<string, HubConfig>
> = { es: ES, pt: PT, fr: FR, de: DE, ja: JA, ko: KO, id: ID, tr: TR };

// next.config.mjs routes the slugs in config/hubs/<locale>.json to
// pages/hub/[slug].tsx; fail loudly at build time if a language's pages and
// its slug map drift apart.
for (const [l, hubs] of Object.entries(LOCALE_HUBS)) {
  const fromJson = Object.values(HUB_SLUGS[l as Locale])
    .sort()
    .join();
  if (Object.keys(hubs).sort().join() !== fromJson) {
    throw new Error(
      `config/hubs/${l}.json does not match config/localeHubs for "${l}"`,
    );
  }
  for (const [slug, h] of Object.entries(hubs)) {
    if (!h.group || HUB_SLUGS[l as Locale][h.group] !== slug) {
      throw new Error(
        `hub ${l}/${slug}: group "${h.group}" is not its key in config/hubs/${l}.json`,
      );
    }
  }
}

// Every localised landing page URL, for the sitemap.
export const LOCALE_HUB_PATHS: string[] = Object.values(LOCALE_HUBS).flatMap(
  (hubs) => Object.values(hubs).map((h) => h.path as string),
);
